'use client';
import React, { useState } from 'react';
import Image from 'next/image';

interface ImageUploadFieldProps {
  field: {
    label?: string;
    placeholder?: string;
  };
  name: string;
  id: string;
  value: string;
  onChange: (value: string) => void;
  readOnly?: boolean;
}

export const ImageUploadField = ({
  field,
  id: _id,
  name: _name,
  onChange,
  readOnly = false,
  value,
}: ImageUploadFieldProps): React.ReactElement => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch('/api/upload', {
        body: formData,
        method: 'POST',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Upload failed');
      }

      const result = await response.json();
      onChange(result.url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleUrlChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.value);
  };

  const handleClearImage = () => {
    onChange('');
    setError(null);
  };

  return (
    <div className="space-y-3">
      {field.label && (
        <label className="block text-sm font-medium text-gray-700">
          {field.label}
        </label>
      )}

      {/* Image Preview */}
      <div className="relative inline-block">
        <div className="relative h-24 w-24 overflow-hidden rounded-lg border border-gray-300">
          <Image
            src={value || '/productPlaceholder.png'}
            alt={value ? 'Preview' : 'Image placeholder'}
            fill
            className="object-cover"
            sizes="96px"
          />
        </div>
        {!readOnly && value && (
          <button
            type="button"
            onClick={handleClearImage}
            className="absolute -top-2 -right-2 flex h-6 w-6 items-center justify-center rounded-full bg-red-500 text-white hover:bg-red-600"
            title="Remove image"
          >
            ×
          </button>
        )}
      </div>

      {!readOnly && (
        <>
          {/* File Upload */}
          <div className="space-y-2">
            <input
              type="file"
              accept="image/*"
              onChange={handleFileUpload}
              disabled={uploading}
              className="block w-full text-sm text-gray-500 file:mr-4 file:rounded-full file:border-0 file:bg-blue-50 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-blue-700 hover:file:bg-blue-100"
            />
            {uploading && <p className="text-sm text-blue-600">Uploading...</p>}
          </div>

          {/* URL Input */}
          <div className="space-y-1">
            <label className="block text-xs text-gray-500">
              Or enter image URL:
            </label>
            <input
              type="url"
              value={value || ''}
              onChange={handleUrlChange}
              placeholder={field.placeholder || 'https://example.com/image.jpg'}
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none"
            />
          </div>
        </>
      )}

      {/* Error Message */}
      {error && <p className="text-sm text-red-600">{error}</p>}
    </div>
  );
};
